import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Loader2, FileText } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import QuoteLineEditor from './quote/QuoteLineEditor';
import QuoteDiscountTvaRow from './quote/QuoteDiscountTvaRow';
import QuoteReferencesDossier from './quote/QuoteReferencesDossier';
import QuoteTotalsBox from './quote/QuoteTotalsBox';

/**
 * Rich devis dialog (creation + edition):
 *  - Company picker (from /companies)
 *  - Learner multi-select (filtered by chosen company)
 *  - Programme combobox (prefills price)
 *  - Billing mode radio (per_learner / per_session)
 *  - Multi-line items editable (qty INT × unit_price)
 *  - Discount: none | percent | amount
 *  - Editable TVA rate
 *  - Live totals (subtotal, discount, HT, TVA, TTC)
 *  - Preview PDF button (WYSIWYG before save)
 *
 * Props:
 *  - open, onOpenChange : Dialog control
 *  - onCreated(doc)     : called after POST (creation)
 *  - onUpdated(doc)     : called after PUT (edition)
 *  - quote              : when set → edit mode (initial values are loaded from this doc)
 */
export default function QuoteFormDialog({ open, onOpenChange, onCreated, onUpdated, quote, prefill, mode: formMode = 'quote' }) {
  const isInvoice = formMode === 'invoice';
  const isEdit = !!quote;
  const DOC_LABEL = isInvoice ? 'facture' : 'devis';
  const DOC_LABEL_U = isInvoice ? 'Facture' : 'Devis';
  const [companies, setCompanies] = useState([]);
  const [learners, setLearners] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [enrollmentsCount, setEnrollmentsCount] = useState({}); // { session_id: count }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [companyId, setCompanyId] = useState('');
  const [particularLearnerId, setParticularLearnerId] = useState('');
  const [mode, setMode] = useState('company'); // 'company' | 'particular'
  const [learnerIds, setLearnerIds] = useState([]);
  const [programmeId, setProgrammeId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [billingMode, setBillingMode] = useState('per_session');
  const [lines, setLines] = useState([{ label: '', quantity: 1, unit_price: 0 }]);
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  const [tvaRate, setTvaRate] = useState(20);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  // In per_session billing, the real learner headcount is independent from the
  // quote line quantity (= 1 single session priced as a bundle). This value is
  // printed on the quote line for transparency and also propagated into the
  // invoice when the devis is converted.
  const [manualLearnerCount, setManualLearnerCount] = useState('');

  // Track whether user manually edited lines so the programme→lines effect doesn't override edits in edit mode
  const [linesTouched, setLinesTouched] = useState(false);

  // Le delai de la maison, lu une fois a l'ouverture. **On ne recalcule aucune
  // date ici** : le serveur la pose, et c'est lui qui a la definition. On
  // affiche seulement la regle, pour qu'on cesse de chercher quoi taper dans
  // un champ qui attend une date alors qu'on pense en jours.
  const [joursParDefaut, setJoursParDefaut] = useState(null);
  // Les listes que l'editeur de lignes propose. **Elles viennent du
  // serveur** : les ecrire ici en ferait une seconde liste a tenir, et
  // deux listes du meme referentiel finissent par ne pas proposer la meme
  // chose.
  const [unites, setUnites] = useState([]);
  const [regimesTva, setRegimesTva] = useState([]);
  const [unitesParMode, setUnitesParMode] = useState({});
  // **Les quatre references du dossier** : c'est ce qu'un financeur
  // rappelle pour payer. Chacune ne s'imprime que si elle est remplie.
  const [conventionRef, setConventionRef] = useState('');
  const [opcoRef, setOpcoRef] = useState('');
  const [dossierRef, setDossierRef] = useState('');
  const [commandeRef, setCommandeRef] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get('/companies').then((r) => r.data).catch(() => []),
      api.get('/learners').then((r) => r.data).catch(() => []),
      api.get('/programs').then((r) => r.data).catch(() => []),
      api.get('/sessions').then((r) => r.data).catch(() => []),
      api.get('/orgs/me').then((r) => r.data).catch(() => null),
      api.get('/edition/referentiels').then((r) => r.data).catch(() => null),
    ]).then(async ([c, l, p, s, org, ref]) => {
      setCompanies(c); setLearners(l); setPrograms(p); setSessions(s || []);
      // Sans reponse, les listes restent vides : l'editeur retombe alors
      // sur « comme le document », qui est le comportement d'avant.
      setUnites(ref?.unites || []);
      setRegimesTva(ref?.regimes_tva || []);
      setUnitesParMode(ref?.unites_par_mode || {});
      const jours = isInvoice ? org?.payment_terms_days : org?.quote_validity_days;
      setJoursParDefaut(Number.isFinite(Number(jours)) ? Number(jours) : null);
      // Seed enrollment counts with the session.learners field (kept in sync by the
      // backend) so the dropdown shows something immediately. We then refine the
      // upcoming sessions with the precise enrolments collection in parallel.
      const seed = Object.fromEntries((s || []).map((x) => [x.id, parseInt(x.learners || 0, 10)]));
      setEnrollmentsCount(seed);
      const upcoming = (s || []).filter((x) => {
        try { return new Date(x.startDate).getTime() >= Date.now() - 24 * 3600 * 1000; }
        catch { return true; }
      }).slice(0, 30); // cap to avoid N HTTP requests on very large tenants
      const results = await Promise.all(
        upcoming.map((sess) =>
          api.get(`/sessions/${sess.id}/learners`)
            .then((r) => [sess.id, Array.isArray(r.data) ? r.data.length : 0])
            .catch(() => [sess.id, seed[sess.id] || 0])
        )
      );
      setEnrollmentsCount((prev) => ({ ...prev, ...Object.fromEntries(results) }));
    }).finally(() => setLoading(false));
    // Reset / preload
    if (isEdit && quote) {
      // Detect mode: if quote has company_id → company mode; else → particular
      const isParticular = !quote.company_id && Array.isArray(quote.learner_ids) && quote.learner_ids.length === 1;
      setMode(isParticular ? 'particular' : 'company');
      setCompanyId(quote.company_id || '');
      setParticularLearnerId(isParticular ? quote.learner_ids[0] : '');
      setLearnerIds(quote.learner_ids || []);
      setProgrammeId(quote.programme_id || '');
      setSessionId(quote.session_id || '');
      setBillingMode(quote.billing_mode || 'per_session');
      const qLines = Array.isArray(quote.lines) && quote.lines.length
        ? quote.lines.map((ln) => ({
            label: ln.label || '',
            quantity: Math.max(1, parseInt(ln.quantity, 10) || 1),
            unit_price: Number(ln.unit_price || 0),
            // **Ce que l'etat ne charge pas, enregistrer l'efface.** Le
            // document les porte ; les oublier ici les remettrait a zero au
            // premier « Enregistrer » sans que rien ne le dise.
            discount_percent: Number(ln.discount_percent || 0),
            unit_code: ln.unit_code || null,
            tva_regime: ln.tva_regime || null,
            tva_mention: ln.tva_mention || null,
          }))
        : [{ label: quote.program || '', quantity: 1, unit_price: Number(quote.amount_ht || quote.amount || 0), discount_percent: 0, unit_code: null, tva_regime: null, tva_mention: null }];
      setLines(qLines);
      setDiscountType(quote.discount_type || 'none');
      setDiscountValue(Number(quote.discount_value || 0));
      setTvaRate(Number(quote.tva_rate ?? 20));
      setValidUntil(quote.validUntil || quote.dueDate || '');
      setConventionRef(quote.convention_ref || '');
      setOpcoRef(quote.opco_ref || '');
      setDossierRef(quote.dossier_ref || '');
      setCommandeRef(quote.commande_ref || '');
      setNotes(quote.notes || '');
      setLinesTouched(true); // don't auto-overwrite lines on programme reselect
    } else {
      setMode(prefill?.mode || 'company');
      setCompanyId(prefill?.company_id || '');
      setParticularLearnerId(prefill?.particular_learner_id || '');
      setLearnerIds(prefill?.learner_ids || []);
      setProgrammeId('');
      setSessionId('');
      setBillingMode('per_session');
      setLines([{ label: '', quantity: 1, unit_price: 0 }]);
      setDiscountType('none'); setDiscountValue(0); setTvaRate(20);
      const d = new Date(); d.setDate(d.getDate() + 30);
      setValidUntil(d.toISOString().slice(0, 10));
      // Phase B/G3 — when navigating from CompanyEmailsTab, the email subject
      // is passed as `prefill.title` and the thread id as `prefill.source_email_thread_id`.
      // Both land in the `notes` field as a reference so the commercial keeps
      // context while drafting the devis.
      if (prefill?.source_email_thread_id || prefill?.title) {
        const lines = [];
        if (prefill?.title) lines.push(`Objet email : ${prefill.title}`);
        if (prefill?.source_email_thread_id) lines.push(`Réf. fil : ${prefill.source_email_thread_id}`);
        setNotes(lines.join('\n'));
      } else {
        setNotes('');
      }
      setManualLearnerCount('');
      setLinesTouched(false);
    }
  }, [open, isEdit, quote, prefill]);

  const company = useMemo(() => companies.find((c) => c.id === companyId), [companies, companyId]);
  const programme = useMemo(() => programs.find((p) => p.id === programmeId), [programs, programmeId]);

  // Sessions that match the selected programme. We accept three linking patterns
  // (programme_id ↔ programs.id, program name equality, session title contains
  // the programme title) so this keeps working with legacy data.
  const programmeSessions = useMemo(() => {
    if (!programme) return [];
    const progTitle = (programme.title || '').toLowerCase();
    const matches = sessions.filter((s) => {
      if (s.programme_id && s.programme_id === programme.id) return true;
      if ((s.program || '').toLowerCase() === progTitle) return true;
      if ((s.title || '').toLowerCase().includes(progTitle) && progTitle.length > 3) return true;
      return false;
    });
    // Sort by startDate ascending; upcoming first
    return matches.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  }, [sessions, programme]);

  const formatSessionOption = (s) => {
    const start = s.startDate ? new Date(s.startDate) : null;
    const fmt = (d) => d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
    const startStr = fmt(start);
    const capacity = parseInt(s.capacity || 0, 10);
    const enrolled = enrollmentsCount[s.id] ?? parseInt(s.learners || 0, 10);
    const remaining = capacity > 0 ? Math.max(0, capacity - enrolled) : null;
    const label = s.title || s.program || s.id;
    const seats = remaining == null
      ? `${enrolled} inscrit${enrolled > 1 ? 's' : ''}`
      : `${remaining} place${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''}`;
    return `${label} · ${startStr || 'Date à définir'} · ${seats}`;
  };

  const companyLearners = useMemo(() => {
    if (!company) return [];
    const cn = (company.name || '').toLowerCase();
    return learners.filter((l) => (l.company || '').toLowerCase() === cn);
  }, [company, learners]);

  const selectedSession = useMemo(
    () => programmeSessions.find((s) => s.id === sessionId),
    [programmeSessions, sessionId],
  );

  const formatSessionDates = (s) => {
    if (!s) return '';
    const fmt = (iso) => {
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString('fr-FR');
      } catch { return ''; }
    };
    const start = fmt(s.startDate);
    const end = fmt(s.endDate);
    const durationSuffix = s.duration ? ` · ${s.duration}h` : '';
    if (start && end && start !== end) return `du ${start} au ${end}${durationSuffix}`;
    if (start) return `le ${start}${durationSuffix}`;
    return '';
  };

  // When programme OR billing mode OR learners count OR selected session change →
  // regenerate default lines. Skipped if user has manually edited lines.
  useEffect(() => {
    if (!programme || linesTouched) return;
    const n = Math.max(1, learnerIds.length);
    const dateSuffix = selectedSession ? ` — ${formatSessionDates(selectedSession)}` : '';
    const locationSuffix = selectedSession?.location ? ` · ${selectedSession.location}` : '';
    if (billingMode === 'per_session') {
      const manualN = parseInt(manualLearnerCount, 10);
      const headcount = Number.isFinite(manualN) && manualN > 0 ? manualN : (learnerIds.length || 0);
      const learnerSuffix = headcount > 0 ? ` · ${headcount} apprenant${headcount > 1 ? 's' : ''}` : '';
      setLines([{
        label: `Session de formation — ${programme.title}${dateSuffix}${locationSuffix}${learnerSuffix}`,
        quantity: 1,
        unit_price: Number(programme.price ?? programme.price_total ?? programme.tarif ?? 0),
      }]);
    } else {
      setLines([{
        label: `Formation ${programme.title} (par apprenant)${dateSuffix}`,
        quantity: n,
        unit_price: Number(programme.price ?? programme.price_per_learner ?? programme.tarif ?? 0),
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, sessionId, billingMode, learnerIds.length, manualLearnerCount]);

  const uniteDeduite = useMemo(() => {
    const code = unitesParMode[billingMode];
    return (unites.find((u) => u.code === code) || {}).libelle || '';
  }, [unites, unitesParMode, billingMode]);

  // **L'apercu refait le calcul du serveur, a l'identique.** Deux
  // arithmetiques pour un seul total finissent par ne pas dire le meme
  // centime, et c'est celui qui s'affiche pendant la saisie qu'on croit. Le
  // serveur le tient dans `_compute_totals` ; on en suit l'ordre pas a pas :
  // le net d'une ligne (sa remise deduite), la remise du document, puis la
  // taxe — a taux unique sur le HT, et **ligne a ligne des que les regimes
  // different**, la remise du document repartie au prorata du net.
  const totals = useMemo(() => {
    const exonere = (code) =>
      !!regimesTva.find((r) => r.code === code && r.categorie === 'E');
    const tauxDeLigne = (ln) =>
      exonere(ln.tva_regime || '') ? 0 : (Number(tvaRate) || 0);
    const netDeLigne = (ln) => {
      const brut = (parseInt(ln.quantity, 10) || 0) * (Number(ln.unit_price) || 0);
      const remise = Math.max(0, Math.min(100, Number(ln.discount_percent) || 0));
      return brut * (1 - remise / 100);
    };
    const r2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
    const subtotal = r2(lines.reduce((s, ln) => s + netDeLigne(ln), 0));
    let discount = 0;
    if (discountType === 'percent') discount = subtotal * Math.max(0, Math.min(100, Number(discountValue) || 0)) / 100;
    else if (discountType === 'amount') discount = Math.max(0, Number(discountValue) || 0);
    discount = r2(Math.min(discount, subtotal));
    const ht = r2(subtotal - discount);
    const taux = new Set(lines.map(tauxDeLigne));
    let tva;
    if (taux.size <= 1) {
      const t = lines.length ? tauxDeLigne(lines[0]) : (Number(tvaRate) || 0);
      tva = r2(ht * t / 100);
    } else {
      const parTaux = new Map();
      lines.forEach((ln) => {
        const t = tauxDeLigne(ln);
        const net = netDeLigne(ln);
        const part = subtotal > 0 ? discount * (net / subtotal) : 0;
        parTaux.set(t, (parTaux.get(t) || 0) + net - part);
      });
      tva = r2([...parTaux.entries()]
        .reduce((s, [t, base]) => s + r2(r2(base) * t / 100), 0));
    }
    const ttc = r2(ht + tva);
    return { subtotal, discount, ht, tva, ttc, plusieursTaux: taux.size > 1 };
  }, [lines, discountType, discountValue, tvaRate, regimesTva]);

  const toggleLearner = (id) => {
    setLearnerIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  };

  const updateLine = (i, field, val) => {
    setLinesTouched(true);
    setLines((L) => L.map((ln, idx) => (idx === i ? { ...ln, [field]: val } : ln)));
  };
  const addLine = () => { setLinesTouched(true); setLines((L) => [...L, { label: '', quantity: 1, unit_price: 0, discount_percent: 0, unit_code: null, tva_regime: null, tva_mention: null }]); };
  const removeLine = (i) => { setLinesTouched(true); setLines((L) => L.filter((_, idx) => idx !== i)); };

  // In particular mode, the learner's full name is the client and we send a single learner_id.
  const particularLearner = useMemo(
    () => learners.find((l) => l.id === particularLearnerId),
    [learners, particularLearnerId]
  );
  // All learners NOT attached to any company (= particuliers)
  const particularLearners = useMemo(
    () => learners.filter((l) => !l.company || l.tag === 'Particulier'),
    [learners]
  );

  const buildPayload = () => {
    const base = {
      programme_id: programmeId || undefined,
      session_id: sessionId || undefined,
      billing_mode: billingMode,
      manual_learner_count: billingMode === 'per_session' && manualLearnerCount
        ? Math.max(0, parseInt(manualLearnerCount, 10) || 0)
        : undefined,
      lines: lines.map((ln) => ({
        label: ln.label || '—',
        quantity: Math.max(1, parseInt(ln.quantity, 10) || 1),
        unit_price: Number(ln.unit_price) || 0,
        discount_percent: Math.max(0, Math.min(100, Number(ln.discount_percent) || 0)),
        unit_code: ln.unit_code || null,
        tva_regime: ln.tva_regime || null,
        // Une mention blanche vaut une mention absente : c'est ce que le
        // serveur refuse, et il vaut mieux le refuser que l'imprimer vide.
        tva_mention: String(ln.tva_mention || '').trim() || null,
      })),
      discount_type: discountType,
      discount_value: Number(discountValue) || 0,
      tva_rate: Number(tvaRate) || 0,
      // Quotes use `validUntil`, invoices use `dueDate`. The same state field
      // `validUntil` holds the chosen ISO date in both modes.
      ...(isInvoice ? { dueDate: validUntil } : { validUntil }),
      convention_ref: conventionRef.trim() || null,
      opco_ref: opcoRef.trim() || null,
      dossier_ref: dossierRef.trim() || null,
      commande_ref: commandeRef.trim() || null,
      notes,
    };
    if (mode === 'particular' && particularLearner) {
      return {
        ...base,
        company_id: null,
        client: `${particularLearner.firstName || ''} ${particularLearner.lastName || ''}`.trim(),
        client_email: particularLearner.email || '',
        learner_ids: [particularLearner.id],
      };
    }
    return { ...base, company_id: companyId, learner_ids: learnerIds };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === 'company' && !companyId) { toast({ title: 'Veuillez sélectionner une entreprise' }); return; }
    if (mode === 'particular' && !particularLearnerId) { toast({ title: 'Veuillez sélectionner un apprenant particulier' }); return; }
    setSubmitting(true);
    try {
      const baseUrl = isInvoice ? '/invoices/advanced' : '/quotes/advanced';
      if (isEdit) {
        const { data } = await api.put(`${baseUrl}/${quote.id}`, buildPayload());
        toast({ title: `${DOC_LABEL_U} modifié${isInvoice ? 'e' : ''}`, description: `${data.id} · ${Number(data.amount || 0).toFixed(2)} € TTC` });
        onUpdated?.(data);
      } else {
        const { data } = await api.post(baseUrl, buildPayload());
        toast({
          title: isInvoice ? 'Facture créée (Provisoire)' : 'Devis créé',
          description: isInvoice
            ? `${data.id} — validez-la pour attribuer un numéro définitif.`
            : `${data.id} · ${Number(data.amount || 0).toFixed(2)} € TTC`,
        });
        onCreated?.(data);
      }
      onOpenChange(false);
    } catch (e2) {
      toast({ title: 'Erreur', description: e2?.response?.data?.detail || 'Opération impossible' });
    }
    setSubmitting(false);
  };

  const handlePreview = async () => {
    if (mode === 'company' && !companyId) { toast({ title: 'Sélectionnez une entreprise pour prévisualiser' }); return; }
    if (mode === 'particular' && !particularLearnerId) { toast({ title: 'Sélectionnez un apprenant pour prévisualiser' }); return; }
    setPreviewing(true);
    try {
      const url = isInvoice ? '/invoices/advanced/preview-pdf' : '/quotes/advanced/preview-pdf';
      const res = await api.post(url, buildPayload(), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de générer l\'aperçu PDF' });
    }
    setPreviewing(false);
  };

  const fmtEur = (n) => `${(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[820px] max-h-[92vh] overflow-y-auto" data-testid="quote-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifier ${isInvoice ? 'la facture' : 'le devis'} ${quote?.id || ''}` : (isInvoice ? 'Nouvelle facture (Provisoire)' : 'Nouveau devis')}</DialogTitle>
          <DialogDescription>Sélectionnez l'entreprise, les apprenants, le programme. Les prix sont pré-remplis automatiquement.{isInvoice ? ' Une facture provisoire est créée — validez-la ensuite pour attribuer un numéro définitif.' : ''}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-[#6B6C85]"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Chargement…</div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            {/* Client type toggle: Entreprise / Particulier */}
            <section>
              <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Type de client</label>
              <div className="inline-flex rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] p-1 mb-3" role="tablist">
                <button
                  type="button"
                  data-testid="quote-mode-company"
                  onClick={() => { setMode('company'); setParticularLearnerId(''); }}
                  className={`h-8 px-4 rounded-lg text-[12.5px] font-semibold transition-colors ${mode === 'company' ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-[#6B6C85] hover:text-[#1A1B2E]'}`}
                >
                  Entreprise
                </button>
                <button
                  type="button"
                  data-testid="quote-mode-particular"
                  onClick={() => { setMode('particular'); setCompanyId(''); setLearnerIds([]); }}
                  className={`h-8 px-4 rounded-lg text-[12.5px] font-semibold transition-colors ${mode === 'particular' ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-[#6B6C85] hover:text-[#1A1B2E]'}`}
                >
                  Particulier
                </button>
              </div>

              {mode === 'company' ? (
                <>
                  <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Entreprise cliente</label>
                  <select
                    data-testid="quote-company"
                    required value={companyId} onChange={(e) => { setCompanyId(e.target.value); setLearnerIds([]); }}
                    className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
                  >
                    <option value="">— Choisir une entreprise —</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
                    Apprenant particulier ({particularLearners.length} dispo.)
                  </label>
                  {particularLearners.length === 0 ? (
                    <div className="text-[12px] text-[#8E8FA5] italic px-3 py-2 bg-[#FAFAFE] rounded-lg border border-dashed border-[#EFF0F6]">
                      Aucun apprenant particulier. Créez un apprenant avec la catégorie « Particulier » depuis la page Apprenants.
                    </div>
                  ) : (
                    <select
                      data-testid="quote-particular-learner"
                      required value={particularLearnerId} onChange={(e) => setParticularLearnerId(e.target.value)}
                      className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
                    >
                      <option value="">— Choisir un apprenant —</option>
                      {particularLearners.map((l) => (
                        <option key={l.id} value={l.id}>{l.firstName} {l.lastName}{l.email ? ` · ${l.email}` : ''}</option>
                      ))}
                    </select>
                  )}
                </>
              )}
            </section>

            {/* Learners */}
            {companyId && (
              <section>
                <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Apprenants à former ({companyLearners.length} dispo.)</label>
                {companyLearners.length === 0 ? (
                  <div className="text-[12px] text-[#8E8FA5] italic px-3 py-2 bg-[#FAFAFE] rounded-lg border border-dashed border-[#EFF0F6]">Aucun apprenant rattaché à cette entreprise. Vous pouvez quand même créer un devis par session.</div>
                ) : (
                  <div className="max-h-[140px] overflow-y-auto space-y-1 pr-2" data-testid="quote-learners-list">
                    {companyLearners.map((l) => (
                      <label key={l.id} className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-colors ${learnerIds.includes(l.id) ? 'bg-[#EEF2FF]' : 'hover:bg-[#FAFAFE]'}`}>
                        <input type="checkbox" checked={learnerIds.includes(l.id)} onChange={() => toggleLearner(l.id)} className="w-4 h-4 rounded accent-[#4F46E5]" data-testid={`quote-learner-${l.id}`} />
                        <div className="w-7 h-7 rounded-full brand-gradient text-white font-semibold flex items-center justify-center text-[10px]">{l.firstName?.[0]}{l.lastName?.[0]}</div>
                        <span className="text-[13px] font-semibold text-[#1A1B2E]">{l.firstName} {l.lastName}</span>
                        <span className="text-[11px] text-[#8E8FA5]">{l.email}</span>
                      </label>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Programme + session + billing mode */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Programme</label>
                <select
                  data-testid="quote-programme"
                  value={programmeId}
                  onChange={(e) => {
                    setProgrammeId(e.target.value);
                    setSessionId(''); // drop any session selection tied to the previous programme
                    setLinesTouched(false);
                  }}
                  className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
                >
                  <option value="">— Aucun programme associé —</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>{p.title} {p.price ? `— ${p.price} €` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
                  Session de formation
                  {programmeId && <span className="ml-1 text-[#8E8FA5] normal-case font-normal">({programmeSessions.length} trouvée{programmeSessions.length > 1 ? 's' : ''})</span>}
                </label>
                <select
                  data-testid="quote-session"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  disabled={!programmeId}
                  className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {!programmeId
                      ? '— Sélectionnez d\'abord un programme —'
                      : programmeSessions.length === 0
                        ? 'Aucune session pour ce programme'
                        : '— Aucune session associée —'}
                  </option>
                  {programmeSessions.map((s) => (
                    <option key={s.id} value={s.id}>{formatSessionOption(s)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Mode de facturation</label>
                <div className="flex gap-2">
                  <label className={`flex-1 flex items-center justify-center gap-1.5 px-2 h-10 rounded-xl border cursor-pointer text-[12px] ${billingMode === 'per_session' ? 'border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5] font-semibold' : 'border-[#EFF0F6] bg-[#F6F7FB] text-[#4A4B63]'}`}>
                    <input type="radio" className="hidden" checked={billingMode === 'per_session'} onChange={() => { setBillingMode('per_session'); setLinesTouched(false); }} data-testid="quote-billing-session" />
                    Par session
                  </label>
                  <label className={`flex-1 flex items-center justify-center gap-1.5 px-2 h-10 rounded-xl border cursor-pointer text-[12px] ${billingMode === 'per_learner' ? 'border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5] font-semibold' : 'border-[#EFF0F6] bg-[#F6F7FB] text-[#4A4B63]'}`}>
                    <input type="radio" className="hidden" checked={billingMode === 'per_learner'} onChange={() => { setBillingMode('per_learner'); setLinesTouched(false); }} data-testid="quote-billing-learner" />
                    Par apprenant
                  </label>
                </div>
              </div>
            </section>

            {/* Manual headcount — only relevant in "per_session" billing */}
            {billingMode === 'per_session' && (
              <section className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3 items-end">
                <div>
                  <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
                    Nombre d'apprenants
                  </label>
                  <input
                    type="number" min="0" step="1"
                    data-testid="quote-manual-learner-count"
                    value={manualLearnerCount}
                    onChange={(e) => { setManualLearnerCount(e.target.value); setLinesTouched(false); }}
                    placeholder={learnerIds.length > 0 ? `${learnerIds.length} inscrits par défaut` : 'Ex: 12'}
                    className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
                  />
                </div>
                <div className="text-[11.5px] text-[#8E8FA5] leading-relaxed pb-2">
                  Apparaît sur la ligne du devis et sur le PDF. Si vide, utilise le nombre d'apprenants
                  rattachés ({learnerIds.length}).
                </div>
              </section>
            )}

            {/* Line items (extracted to QuoteLineEditor) */}
            <QuoteLineEditor
              lines={lines}
              onAdd={addLine}
              onUpdate={updateLine}
              onRemove={removeLine}
              unites={unites}
              regimesTva={regimesTva}
              uniteDeduite={uniteDeduite}
            />

            {/* Discount + TVA + Validity (extracted to QuoteDiscountTvaRow) */}
            <QuoteDiscountTvaRow
              discountType={discountType}
              onDiscountTypeChange={setDiscountType}
              discountValue={discountValue}
              onDiscountValueChange={setDiscountValue}
              tvaRate={tvaRate}
              onTvaRateChange={setTvaRate}
              validUntil={validUntil}
              onValidUntilChange={setValidUntil}
              isInvoice={isInvoice}
              joursParDefaut={joursParDefaut}
            />

            {/* Totals (extracted to QuoteTotalsBox) */}
            <QuoteTotalsBox totals={totals} tvaRate={tvaRate} fmtEur={fmtEur} plusieursTaux={totals.plusieursTaux} />

            {/* **Les references du dossier**, dans leur composant : c'est
                celui que la page d'apercu montre, donc ce qu'on regarde est ce
                qui sera a l'ecran. */}
            <QuoteReferencesDossier
              conventionRef={conventionRef} onConventionRef={setConventionRef}
              opcoRef={opcoRef} onOpcoRef={setOpcoRef}
              dossierRef={dossierRef} onDossierRef={setDossierRef}
              commandeRef={commandeRef} onCommandeRef={setCommandeRef}
            />

            {/* Notes */}
            <section>
              <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Notes (visibles sur le PDF)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] focus:bg-white focus:border-[#4F46E5] outline-none resize-none" placeholder="Conditions particulières, modalités de règlement, etc." />
            </section>

            <DialogFooter className="flex-wrap gap-2">
              <button type="button" onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-xl bg-[#F6F7FB] text-[#4A4B63] text-[13px] font-semibold">Annuler</button>
              <button
                type="button" onClick={handlePreview} disabled={previewing || submitting}
                data-testid="quote-preview-pdf"
                className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-60"
              >
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Prévisualiser le PDF
              </button>
              <button type="submit" disabled={submitting || previewing} data-testid="quote-submit" className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5 shadow-sm disabled:opacity-60">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? 'Enregistrer' : (isInvoice ? 'Créer la facture' : 'Créer le devis')}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
